import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tipoCajaAPI, handleAPIError } from '../../services/api';

// Thunks para operaciones asíncronas
export const fetchTiposCaja = createAsyncThunk(
  'tipoCaja/fetchTiposCaja',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await tipoCajaAPI.getAll(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const fetchTipoCajaById = createAsyncThunk(
  'tipoCaja/fetchTipoCajaById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await tipoCajaAPI.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const createTipoCaja = createAsyncThunk(
  'tipoCaja/createTipoCaja',
  async (tipoCajaData, { rejectWithValue }) => {
    try {
      const response = await tipoCajaAPI.create(tipoCajaData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const updateTipoCaja = createAsyncThunk(
  'tipoCaja/updateTipoCaja',
  async ({ id, tipoCajaData }, { rejectWithValue }) => {
    try {
      const response = await tipoCajaAPI.update(id, tipoCajaData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const deleteTipoCaja = createAsyncThunk(
  'tipoCaja/deleteTipoCaja',
  async (id, { rejectWithValue }) => {
    try {
      await tipoCajaAPI.hardDelete(id);
      return id;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

const initialState = {
  tiposCaja: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    activo: undefined
  }
};

const tipoCajaSlice = createSlice({
  name: 'tipoCaja',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        activo: undefined
      };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch tipos de caja
      .addCase(fetchTiposCaja.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTiposCaja.fulfilled, (state, action) => {
        state.loading = false;
        state.tiposCaja = action.payload;
      })
      .addCase(fetchTiposCaja.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch tipo de caja by ID
      .addCase(fetchTipoCajaById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTipoCajaById.fulfilled, (state, action) => {
        state.loading = false;
        // Actualizar o agregar el tipo de caja en el array
        const index = state.tiposCaja.findIndex(tipo => tipo.id === action.payload.id);
        if (index !== -1) {
          state.tiposCaja[index] = action.payload;
        } else {
          state.tiposCaja.push(action.payload);
        }
      })
      .addCase(fetchTipoCajaById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create tipo de caja
      .addCase(createTipoCaja.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTipoCaja.fulfilled, (state, action) => {
        state.loading = false;
        state.tiposCaja.push(action.payload);
      })
      .addCase(createTipoCaja.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update tipo de caja
      .addCase(updateTipoCaja.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTipoCaja.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tiposCaja.findIndex(tipo => tipo.id === action.payload.id);
        if (index !== -1) {
          state.tiposCaja[index] = action.payload;
        }
      })
      .addCase(updateTipoCaja.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete tipo de caja
      .addCase(deleteTipoCaja.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTipoCaja.fulfilled, (state, action) => {
        state.loading = false;
        // Eliminar físicamente del array (hard delete)
        state.tiposCaja = state.tiposCaja.filter(tipo => tipo.id !== action.payload);
      })
      .addCase(deleteTipoCaja.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  clearError
} = tipoCajaSlice.actions;

// Selectors
export const selectAllTiposCaja = (state) => state.tipoCaja.tiposCaja;
export const selectTiposCajaLoading = (state) => state.tipoCaja.loading;
export const selectTiposCajaError = (state) => state.tipoCaja.error;
export const selectTiposCajaFilters = (state) => state.tipoCaja.filters;

export const selectActiveTiposCaja = (state) => {
  return state.tipoCaja.tiposCaja.filter(tipo => tipo.activo);
};

export const selectFilteredTiposCaja = (state) => {
  const tiposCaja = selectAllTiposCaja(state);
  const filters = selectTiposCajaFilters(state);
  
  return tiposCaja.filter(tipo => {
    const matchesSearch = !filters.search || 
      tipo.tipo?.toLowerCase().includes(filters.search.toLowerCase()) ||
      tipo.descripcion?.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesActivo = filters.activo === undefined || tipo.activo === filters.activo;
    
    return matchesSearch && matchesActivo;
  });
};

export const selectTipoCajaById = (state, tipoCajaId) => {
  return state.tipoCaja.tiposCaja.find(tipo => tipo.id === tipoCajaId);
};

export default tipoCajaSlice.reducer;
